import { _js } from '@ifixit/localize';
import { Fragment, useContext, useEffect } from 'react';
import { ViewGuidesButton, OrderPartButton } from '../MiscParts/part_buttons';
import CopyIcon from '../Generic/clipboard';
import { HotSpot } from 'Guide/Schematics/Diagram/Providers/dataContext';
import { TooltipContext } from '../Providers/tooltipContext';
import { KeypressContext } from '../Providers/keypressContext';
import { fromWiki } from '../Generic/fromWiki';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';
import { Box, Flex, HStack, Link, Show, VStack } from '@chakra-ui/react';

const ItemContent = ({ data }: { data: iFixitHotSpotItem }) => {
   const name = fromWiki(data, 'name') || data.description;
   const sku = fromWiki(data, 'sku') || '';
   const brand = fromWiki(data, 'brand') || '';

   const itemWikiUrl = fromWiki(data, 'url') || '';
   const itemImageUrl = fromWiki(data, 'imageUrl');

   const copyText = `${brand} ${data.description} ${sku}`;

   return (
      <Fragment>
         {itemImageUrl && (
            <Show above="sm">
               <Box
                  paddingTop="75%"
                  backgroundSize="cover"
                  borderRadius="sm"
                  marginBottom="8px"
                  style={{ backgroundImage: `url(${itemImageUrl})` }}
               />
            </Show>
         )}
         <Flex alignItems="center">
            <Link
               href={itemWikiUrl}
               isExternal={true}
               fontWeight="bold"
               fontSize="16px"
               textAlign="center"
               color="gray.800"
               paddingRight="8px"
               _hover={{
                  textDecoration: 'none',
               }}
               _visited={{
                  color: 'gray.800',
               }}
            >
               {name}
            </Link>
            <CopyIcon copyText={copyText} />
         </Flex>
         <VStack spacing="8px" alignItems="flex-start">
            {sku && (
               <Box fontSize="14px" textAlign="left" color="gray.600">
                  {_js('Part Number:') + ' ' + sku}
               </Box>
            )}
            {brand && (
               <Box fontSize="14px" textAlign="left" color="gray.600">
                  {_js('Brand:') + ' ' + brand}
               </Box>
            )}
            <HStack spacing="10px">
               <OrderPartButton data={data} />
               <ViewGuidesButton data={data} />
            </HStack>
         </VStack>
      </Fragment>
   );
};

const HotspotTooltipContent = ({ data: group }: { data: HotSpot<iFixitHotSpotItem> }) => {
   const lastIndex = group.items.length - 1;
   const renderedItems = group.items.map((item, index) => {
      const hasSep = index != lastIndex;
      const seperator = hasSep ? (
         <Box
            margin="12px 16px"
            borderTopWidth={1}
            borderColor="gray.300"
            width="100%"
            height="1px"
         />
      ) : null;

      // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook cannot be called inside a callback.
      const tooltipContext = useContext(TooltipContext);
      // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook cannot be called inside a callback.
      const keypressContext = useContext(KeypressContext);
      // eslint-disable-next-line react-hooks/rules-of-hooks -- FIXME: React Hook cannot be called inside a callback.
      useEffect(() => {
         const closeTooltips = () => {
            tooltipContext.setOpenDataPointId(null);
         };

         const mapping = {
            id: 'Tooltip',
            key: 'Escape',
            action: closeTooltips,
         };

         keypressContext.pushMapping(mapping);

         return () => {
            keypressContext.popMapping(mapping);
         };
      }, [
         keypressContext.pushMapping,
         keypressContext.popMapping,
         tooltipContext.setOpenDataPointId,
      ]);

      return (
         <Fragment key={item.id}>
            <ItemContent data={item} />
            {seperator}
         </Fragment>
      );
   });

   return <Fragment>{renderedItems}</Fragment>;
};

export default HotspotTooltipContent;
